// TutorialPanel.js — v6 : textes SEO, photos Unsplash, typographie grande

import React, { useState, useEffect } from 'react';

// Photos Unsplash — libres de droits, pas de CORS
const PHOTOS = [
  'https://images.unsplash.com/photo-1539650116574-8efeb43e2750?w=800&q=80', // pyramides
  'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80',    // Rome colisée
  'https://images.unsplash.com/photo-1541535650810-10d26f5c2ab3?w=800&q=80', // atlas globe antique
  'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80', // vue aérienne terre
  'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&q=80', // terre depuis espace
];

// Steps par défaut — remplacés par ceux de Supabase si disponibles
const STEPS = [
  {
    icon: '🌍',
    title: 'Bienvenue sur\nKronotera',
    subtitle: 'Prêt à voyager dans le temps ?',
    desc: 'Kronotera est une plateforme interactive qui vous permet d\'explorer l\'Histoire mondiale sur un globe en 3D. Parcourez plus de 5000 ans d\'événements historiques — guerres, découvertes scientifiques, révolutions politiques, explorations — géolocalisés avec précision sur la Terre.',
    tip: 'Faites pivoter le globe en cliquant-glissant, zoomez avec la molette.',
    era: 'De −3500 av. J.-C. à aujourd\'hui',
    grad: ['#000814', '#001d3d', '#003566'],
    accent: '#4488ff',
    photo: PHOTOS[0],
  },
  {
    icon: '⏱️',
    title: 'La timeline\nhistorique',
    subtitle: 'Naviguez dans 5500 ans d\'histoire',
    desc: 'Le curseur en bas de l\'écran est votre machine à voyager dans le temps. Faites-le glisser pour choisir une année entre −3500 et 2024. Les événements historiques les plus proches de l\'année sélectionnée apparaissent automatiquement sur le globe, localisés à leur emplacement exact.',
    tip: 'Les boutons "Évén. préc." et "Évén. suiv." vous font sauter précisément d\'événement en événement, sans manquer aucun moment clé.',
    era: 'Timeline interactive −3500 → 2024',
    grad: ['#000814', '#001d3d', '#003566'],
    accent: '#4488ff',
    photo: PHOTOS[3],
  },
  {
    icon: '📍',
    title: 'Explorez\nles événements',
    subtitle: 'Chaque point raconte une histoire',
    desc: 'Sur le globe, chaque point lumineux représente un événement historique géolocalisé. La couleur indique sa catégorie : rouge pour les conflits armés, bleu pour les découvertes scientifiques, vert pour les grandes explorations, or pour la politique, violet pour la culture et les arts.',
    tip: 'Cliquez sur n\'importe quel point pour ouvrir sa fiche complète avec photo Wikipedia, contexte historique détaillé, et liens vers les événements connexes.',
    era: '90+ événements référencés',
    grad: ['#0e0005', '#3b0012', '#7a0025'],
    accent: '#e84060',
    photo: PHOTOS[2],
  },
  {
    icon: '🔍',
    title: 'Recherche\net filtres',
    subtitle: 'Trouvez n\'importe quel événement',
    desc: 'La barre de recherche (raccourci ⌘K ou Ctrl+K) vous permet de trouver instantanément n\'importe quel événement historique par son nom, sa description ou son année. Les filtres par catégorie vous permettent de ne visualiser que les guerres, ou uniquement les révolutions scientifiques.',
    tip: 'Activez le bouton 🔗 pour voir apparaître les arcs lumineux reliant les événements historiquement connectés — cause à effet, influence, même époque.',
    era: '7 catégories thématiques',
    grad: ['#00100a', '#003d22', '#006b3a'],
    accent: '#00c96b',
    photo: PHOTOS[1],
  },
  {
    icon: '✚',
    title: 'Contribuez\nà l\'Histoire',
    subtitle: 'Devenez auteur de l\'Histoire mondiale',
    desc: 'Kronotera est une plateforme collaborative. Si vous connaissez un événement historique manquant — local, méconnu ou récent — créez un compte gratuitement et soumettez votre contribution. Chaque événement soumis est vérifié par notre équipe avant d\'être visible sur le globe pour tous les utilisateurs.',
    tip: 'Il vous suffit d\'un titre, d\'une date, de coordonnées géographiques et d\'une description. Vos événements approuvés enrichissent la mémoire collective mondiale.',
    era: 'Communauté mondiale ouverte',
    grad: ['#0a0014', '#2d0054', '#4b0082'],
    accent: '#9966ff',
    photo: PHOTOS[4],
  },
];

export function TutorialPanel({ onClose, inPanel = false }) {
  const [step, setStep]       = useState(0);
  const [visible, setVisible] = useState(false);
  const [imgOk, setImgOk]     = useState(true);

  useEffect(() => { setTimeout(() => setVisible(true), 60); }, []);
  useEffect(() => { setImgOk(true); }, [step]);

  const goTo   = (i) => setStep(Math.max(0, Math.min(STEPS.length - 1, i)));
  const next   = () => step < STEPS.length - 1 ? goTo(step + 1) : onClose();
  const prev   = () => goTo(step - 1);
  const isLast = step === STEPS.length - 1;
  const cur    = STEPS[step];

  if (inPanel) {
    return (
      <div
        className="event-panel tuto-panel-wrap"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateX(0)' : 'translateX(20px)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {/* ══ HERO ══════════════════════════════════════════════ */}
        <div
          className="tuto-hero"
          style={{
            background: imgOk
              ? 'transparent'
              : `linear-gradient(150deg, ${cur.grad[2]} 0%, ${cur.grad[1]} 50%, ${cur.grad[0]} 100%)`,
          }}
        >
          {/* Photo de fond */}
          {imgOk && (
            <img
              src={cur.photo}
              alt=""
              className="tuto-photo-bg"
              onError={() => setImgOk(false)}
            />
          )}

          {/* Overlay sombre pour lisibilité */}
          <div className="tuto-photo-overlay" style={{
            background: `linear-gradient(180deg,
              rgba(0,0,0,0.25) 0%,
              rgba(0,0,0,0.15) 30%,
              rgba(0,0,0,0.55) 65%,
              rgba(5,8,16,0.95) 100%)`
          }} />

          {/* Top : icône + fermer — poignée drag */}
          <div className="tuto-hero-top drag-handle" style={{ cursor: 'grab' }}>
            <span className="tuto-hero-emoji">{cur.icon}</span>
            <button className="tuto-close-btn" onClick={onClose}>×</button>
          </div>

          {/* Titre GRAND Arial Black */}
          <div className="tuto-hero-title">
            {cur.title.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>

          {/* Sous-titre */}
          <div className="tuto-hero-sub" style={{ color: cur.accent }}>
            {cur.subtitle}
          </div>

          {/* Tag époque */}
          <div className="tuto-era-tag" style={{
            background: `${cur.accent}22`,
            borderColor: `${cur.accent}55`,
            color: cur.accent
          }}>
            {cur.era}
          </div>

          {/* Barre de progression */}
          <div className="tuto-progress-bar">
            <div className="tuto-progress-fill"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%`, background: cur.accent }} />
          </div>
        </div>

        {/* ══ CORPS ════════════════════════════════════════════ */}
        <div className="tuto-body">
          <p className="tuto-desc-text">{cur.desc}</p>

          {cur.tip && (
            <div className="tuto-tip-box" style={{ borderLeftColor: cur.accent }}>
              <span>💡</span>
              <span>{cur.tip}</span>
            </div>
          )}

          {/* Dots + counter */}
          <div className="tuto-dots-row">
            {STEPS.map((_, i) => (
              <button key={i} className="tuto-dot-pill" onClick={() => goTo(i)}
                style={{
                  width: i === step ? 24 : 8,
                  background: i === step ? cur.accent : i < step ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.15)',
                }} />
            ))}
            <span className="tuto-step-txt">{step + 1} / {STEPS.length}</span>
          </div>

          {/* Boutons navigation */}
          <div className="tuto-nav">
            {step > 0 && (
              <button className="tuto-btn-prev" onClick={prev}>← Retour</button>
            )}
            <button className="tuto-btn-main" onClick={next}
              style={{ background: cur.accent }}>
              {isLast ? '✓ C\'est parti !' : 'Suivant →'}
            </button>
          </div>

          {!isLast && (
            <button className="tuto-skip-link" onClick={onClose}>Passer le tutoriel</button>
          )}
        </div>
      </div>
    );
  }

  // Mode flottant (bouton ❓ topbar)
  return (
    <div className={`tutorial-panel ${visible ? 'tuto-in' : ''}`}>
      <button className="tuto-close" onClick={onClose}>×</button>
      <div style={{ fontSize: 32, marginBottom: 8 }}>{cur.icon}</div>
      <h3 className="tuto-title" style={{ fontSize: 18, marginBottom: 8 }}>{cur.title.replace('\n', ' ')}</h3>
      <p className="tuto-desc" style={{ fontSize: 13, lineHeight: 1.6 }}>{cur.desc}</p>
      {cur.tip && <div className="tuto-tip"><span>💡</span><span style={{ fontSize: 12 }}>{cur.tip}</span></div>}
      <div className="tuto-dots" style={{ margin: '12px 0' }}>
        {STEPS.map((_, i) => (
          <button key={i} className={`tuto-dot ${i === step ? 'active' : ''}`} onClick={() => goTo(i)} />
        ))}
      </div>
      <div className="tuto-nav">
        {step > 0 && <button className="tuto-btn tuto-btn-prev" onClick={prev}>← Retour</button>}
        <button className="tuto-btn tuto-btn-next" onClick={next}>
          {isLast ? '✓ C\'est parti !' : 'Suivant →'}
        </button>
      </div>
      {!isLast && <button className="tuto-skip" onClick={onClose}>Passer</button>}
    </div>
  );
}
